import type {
  LeverOverlapDeclaration,
  LeverOverlapEvidenceRequirement,
  LeverOverlapRelation,
} from './lever-overlap-registry';

/**
 * Behavior-preserving extraction (P1 follow-up, head 22504caadb; function-size
 * follow-up, head 4214f74226): the ORIGINAL sga_spend-aggregate, risk_claims,
 * org_cost, segment-lens, and cost-to-serve declaration logic that used to
 * live inline in `lever-overlap-registry.ts`'s `buildDeclarations()`, moved
 * out verbatim (same declarations, same order, same results), then split
 * into per-group functions of <=60 lines composed by a <=60-line parent.
 * Pure functions - the family ids they need are passed in by the caller, so
 * this file carries no runtime import of `lever-overlap-registry.ts` - only
 * the type-only import above (erased at compile time) - avoiding a circular
 * require.
 */

function decl(
  familyA: string,
  familyB: string,
  relation: LeverOverlapRelation,
  basis: string,
  evidenceRequirement?: LeverOverlapEvidenceRequirement
): LeverOverlapDeclaration {
  return Object.freeze({
    familyA,
    familyB,
    relation,
    evidenceRequirement:
      evidenceRequirement ?? (relation === 'overlapping' ? 'deconfliction_receipt' : 'structural_policy'),
    basis,
  });
}

// `one` declared against every member of `many`, in array order - the shape
// shared by every "one family vs a whole cluster" declaration below.
function oneVsMany(
  one: string,
  many: readonly string[],
  relation: LeverOverlapRelation,
  basis: string
): LeverOverlapDeclaration[] {
  return many.map((other) => decl(one, other, relation, basis));
}

// The certified SG&A aggregate draws from exactly the eight granular vendor pools, so it
// inherits their disjoint posture against labor, revenue, and purchased transportation — and it
// OVERLAPS its own leaves one-for-one (the aggregate replaces the leaves; summing both
// double-counts the same pools). Cargo claims-expense recovery resolves against a different pool
// (5300_cargo_claims_expense); that pairing needs an immutable cut-bound zero-overlap receipt.
function sgaAggregateDeclarations(
  sgaAggregateFamily: string,
  laborCostFamilies: readonly string[],
  revenueSideFamilies: readonly string[],
  purchasedTransportFamilies: readonly string[],
  sgaSpendFamilies: readonly string[],
  riskClaimsFamily: string
): LeverOverlapDeclaration[] {
  return [
    ...oneVsMany(
      sgaAggregateFamily,
      laborCostFamilies,
      'disjoint',
      'Non-labor SG&A vendor spend does not share dollars with internal labor cost programs.'
    ),
    ...oneVsMany(
      sgaAggregateFamily,
      revenueSideFamilies,
      'disjoint',
      'Non-labor SG&A vendor spend does not share dollars with revenue realization.'
    ),
    ...oneVsMany(
      sgaAggregateFamily,
      purchasedTransportFamilies,
      'disjoint',
      'Corporate SG&A spend is not purchased transportation.'
    ),
    ...oneVsMany(
      sgaAggregateFamily,
      sgaSpendFamilies,
      'overlapping',
      'The certified SG&A aggregate replaces its cost-pool leaf claims; summing both double-counts the same vendor pools.'
    ),
    decl(
      sgaAggregateFamily,
      riskClaimsFamily,
      'disjoint',
      'Cargo claims-expense recovery is expected to resolve against the claims and insurance loss pool (5300_cargo_claims_expense), while the certified corporate SG&A aggregate resolves against its eight non-labor vendor cost pools. This policy row is not evidence; publication requires an immutable cut-bound zero-overlap receipt for the exact member universes.',
      'immutable_zero_overlap_receipt'
    ),
  ];
}

// productivity_lean_ai x risk_claims (#4008 S9). Automation cost-release resolves against the
// governed operations process cost accounts (gl_account:6010-6016), while cargo-claims recovery
// resolves against the claims and insurance loss pool (5300_cargo_claims_expense). The pair is
// declared receipt-required ('overlapping' posture, the member-proof path): the two contributions
// are NEVER additive on policy alone — a cut-bound zero-overlap exclusion receipt over the exact
// member universes must cover the pair, exactly like the segment-lens pairs. (Deliberately NOT
// 'disjoint' + immutable_zero_overlap_receipt: that disposition path additionally requires
// attributed pool-footprint member universes on BOTH live impact claims, which the productivity
// envelope does not emit — the declaration would be inert and the pair would stay fail-closed
// forever despite a valid receipt.) The first covering receipt is minted and readback-verified
// live (artifact `allocation-lens-exclusion-receipt:cms56tfx3001mqparqss5upau`, verdict
// zero_overlap, enterprise / FY2026-Q2 / atomic-pnl-cut|FY2026-Q2|2026-06-30, connector-derived
// member universes 7 x 1, overlapMemberCount 0).
function productivityLeanAiVsRiskClaims(riskClaimsFamily: string): LeverOverlapDeclaration[] {
  return [
    decl(
      'productivity_lean_ai',
      riskClaimsFamily,
      'overlapping',
      'Productivity automation cost-release (governed operations process cost accounts gl_account:6010-6016) and cargo claims-expense recovery (claims and insurance loss pool 5300_cargo_claims_expense) are never additive on policy alone; an approved cut-bound zero-overlap exclusion receipt over the exact member universes is required before compositing.'
    ),
  ];
}

// The non-workforce org-cost initiative pool overlaps EVERY registered family until an approved
// exclusion receipt proves its initiative dollars are separate. Its keys are incommensurable with
// every counterpart vocabulary, so neither a structural-disjoint declaration nor member-level
// proof can ever stand in for the receipt (vacuous-basis guard). In particular it is NOT disjoint
// from sga_spend: initiative dollars can share vendor/pool dollars with the certified SG&A pools.
function orgCostDeclarations(
  orgCostFamily: string,
  laborCostFamilies: readonly string[],
  revenueSideFamilies: readonly string[],
  purchasedTransportFamilies: readonly string[],
  sgaSpendFamilies: readonly string[],
  sgaAggregateFamily: string,
  riskClaimsFamily: string,
  costToServeFamily: string
): LeverOverlapDeclaration[] {
  return [
    ...oneVsMany(
      orgCostFamily,
      laborCostFamilies,
      'overlapping',
      'The org-cost initiative pool and internal labor programs act on the same org operating model; shared-scope receipts required.'
    ),
    ...oneVsMany(
      orgCostFamily,
      revenueSideFamilies,
      'overlapping',
      'Org-cost initiative dollars are not proven separate from revenue-realization programs; member keys are incommensurable, so an exclusion receipt is required.'
    ),
    ...oneVsMany(
      orgCostFamily,
      purchasedTransportFamilies,
      'overlapping',
      'Org-cost initiative dollars are not proven separate from purchased-transportation recovery; member keys are incommensurable, so an exclusion receipt is required.'
    ),
    ...oneVsMany(
      orgCostFamily,
      sgaSpendFamilies,
      'overlapping',
      'Non-workforce org-cost initiatives can draw on the same vendor and cost pools as SG&A spend; shared-scope receipts required.'
    ),
    decl(
      orgCostFamily,
      sgaAggregateFamily,
      'overlapping',
      'Non-workforce org-cost initiatives can draw on the same vendor and cost pools as the certified SG&A aggregate; shared-scope receipts required.'
    ),
    decl(
      orgCostFamily,
      riskClaimsFamily,
      'overlapping',
      'Org-cost initiative dollars are not proven separate from claims-expense recovery; member keys are incommensurable, so an exclusion receipt is required.'
    ),
    decl(
      orgCostFamily,
      costToServeFamily,
      'overlapping',
      'Org-cost initiative dollars are not proven separate from cost-to-serve account repricing; member keys are incommensurable, so an exclusion receipt is required.'
    ),
  ];
}

// Segment margin is an allocation lens: it overlaps EVERY dollar family until its contribution is
// tied to a named underlying action with an exclusion receipt.
function segmentLensVsEveryFamily(
  segmentLensFamily: string,
  laborCostFamilies: readonly string[],
  revenueSideFamilies: readonly string[],
  purchasedTransportFamilies: readonly string[],
  sgaSpendFamilies: readonly string[],
  sgaAggregateFamily: string,
  riskClaimsFamily: string,
  orgCostFamily: string
): LeverOverlapDeclaration[] {
  const everyFamily = [
    ...laborCostFamilies,
    ...revenueSideFamilies,
    ...purchasedTransportFamilies,
    ...sgaSpendFamilies,
    sgaAggregateFamily,
    riskClaimsFamily,
    orgCostFamily,
  ];
  return oneVsMany(
    segmentLensFamily,
    everyFamily,
    'overlapping',
    'Segment margin is an allocation lens; cleanup dollars must prove exclusion from underlying lever actions.'
  );
}

// Cost-to-serve account repricing shares the customer-account revenue realization dimension
// (gl_account revenue lines) with every revenue-side pricing lever, so each pair overlaps and a
// de-confliction receipt is required before compositing. Against internal-labor,
// purchased-transport, and non-labor SG&A vendor pools, cost-to-serve repricing draws on
// structurally different gl_accounts and is additive (disjoint).
function costToServeDeclarations(
  costToServeFamily: string,
  revenueSideFamilies: readonly string[],
  laborCostFamilies: readonly string[],
  purchasedTransportFamilies: readonly string[],
  sgaSpendFamilies: readonly string[]
): LeverOverlapDeclaration[] {
  return [
    ...oneVsMany(
      costToServeFamily,
      revenueSideFamilies,
      'overlapping',
      'Cost-to-serve account repricing and revenue-side pricing both act on the same customer-account revenue realization (shared gl_account revenue lines); shared-scope receipts required.'
    ),
    ...oneVsMany(
      costToServeFamily,
      laborCostFamilies,
      'disjoint',
      'Customer-account revenue repricing does not share dollars with internal labor cost pools.'
    ),
    ...oneVsMany(
      costToServeFamily,
      purchasedTransportFamilies,
      'disjoint',
      'Customer-account revenue repricing does not share dollars with purchased-transportation buy-side spend.'
    ),
    ...oneVsMany(
      costToServeFamily,
      sgaSpendFamilies,
      'disjoint',
      'Customer-account revenue repricing does not share dollars with non-labor SG&A vendor spend.'
    ),
  ];
}

// Segment margin is an allocation lens; it overlaps cost-to-serve until its
// contribution is tied to a named underlying action with an exclusion receipt.
function segmentLensVsCostToServe(
  segmentLensFamily: string,
  costToServeFamily: string
): LeverOverlapDeclaration[] {
  return [
    decl(
      segmentLensFamily,
      costToServeFamily,
      'overlapping',
      'Segment margin is an allocation lens; cost-to-serve cleanup dollars must prove exclusion from underlying revenue-side lever actions.'
    ),
  ];
}

export type AggregateAndLensFamilies = Readonly<{
  laborCostFamilies: readonly string[];
  revenueSideFamilies: readonly string[];
  purchasedTransportFamilies: readonly string[];
  sgaSpendFamilies: readonly string[];
  segmentLensFamily: string;
  riskClaimsFamily: string;
  sgaAggregateFamily: string;
  costToServeFamily: string;
  orgCostFamily: string;
}>;

// Parent: composes the groups above in the ORIGINAL block order (aggregate,
// productivity_lean_ai x risk_claims, org_cost, segment-lens-vs-everything,
// cost-to-serve, then the trailing segment-lens-vs-cost-to-serve single
// declaration that closed the pre-split function).
export function buildAggregateAndLensDeclarations(
  families: AggregateAndLensFamilies
): readonly LeverOverlapDeclaration[] {
  const {
    laborCostFamilies,
    revenueSideFamilies,
    purchasedTransportFamilies,
    sgaSpendFamilies,
    segmentLensFamily,
    riskClaimsFamily,
    sgaAggregateFamily,
    costToServeFamily,
    orgCostFamily,
  } = families;

  return [
    ...sgaAggregateDeclarations(
      sgaAggregateFamily,
      laborCostFamilies,
      revenueSideFamilies,
      purchasedTransportFamilies,
      sgaSpendFamilies,
      riskClaimsFamily
    ),
    ...productivityLeanAiVsRiskClaims(riskClaimsFamily),
    ...orgCostDeclarations(
      orgCostFamily,
      laborCostFamilies,
      revenueSideFamilies,
      purchasedTransportFamilies,
      sgaSpendFamilies,
      sgaAggregateFamily,
      riskClaimsFamily,
      costToServeFamily
    ),
    ...segmentLensVsEveryFamily(
      segmentLensFamily,
      laborCostFamilies,
      revenueSideFamilies,
      purchasedTransportFamilies,
      sgaSpendFamilies,
      sgaAggregateFamily,
      riskClaimsFamily,
      orgCostFamily
    ),
    ...costToServeDeclarations(
      costToServeFamily,
      revenueSideFamilies,
      laborCostFamilies,
      purchasedTransportFamilies,
      sgaSpendFamilies
    ),
    ...segmentLensVsCostToServe(segmentLensFamily, costToServeFamily),
  ];
}
